import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Breadcrumb, Spin, Empty } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { sectionService } from '../services/section.service';
import { categoryService, Category } from '../services/category.service';
import { SectionResponse, ProductInSection } from '../types/section';
import { Product } from '../types/product';
import ProductCard from '../components/products/ProductCard';
import { ProductItem } from '../services/product.service';

const SectionPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [section, setSection] = useState<SectionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productsWithCategories, setProductsWithCategories] = useState<
    ProductItem[]
  >([]);

  // Fetch categories for mapping category_id to category name
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await categoryService.getPublicCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchSectionData = async () => {
      setLoading(true);
      try {
        if (!slug) {
          throw new Error('Slug not provided');
        }
        const data = await sectionService.getSectionBySlug(slug);
        setSection(data);

        // If we have categories data, map products with proper category names
        if (categories.length > 0 && data.products.length > 0) {
          const mappedProducts = data.products.map((product) =>
            mapToProductItem(product, categories)
          );
          setProductsWithCategories(mappedProducts);
        }
      } catch (err) {
        console.error('Error fetching section data:', err);
        setError('Không thể tải dữ liệu. Vui lòng thử lại sau.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchSectionData();
    }
  }, [slug, categories]);

  // Convert ProductInSection to ProductItem format for ProductCard component
  const mapToProductItem = (
    product: ProductInSection,
    categoriesList: Category[]
  ): ProductItem => {
    // Find category name from category_id
    const categoryName = product.category_id
      ? categoriesList.find((cat) => cat._id === product.category_id)?.name ||
        ''
      : '';

    return {
      id: product._id,
      name: product.name,
      description: product.description || '',
      category: categoryName, // Use the category name instead of ID
      images: product.images || [],
      unit: product.unit || '',
      original_price: product.pricing?.original_price || 0,
      current_price:
        product.pricing?.sale_price || product.pricing?.original_price || 0,
      is_on_sale:
        (product.pricing?.sale_price !== undefined &&
          product.pricing.sale_price !== product.pricing?.original_price) ||
        false,
      is_trending: product.is_trending || false,
      slug: product._id, // Using _id as slug since it's not provided in the API
      status: product.status,
    };
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-[400px]'>
        <Spin size='large' />
      </div>
    );
  }

  if (error || !section) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='bg-red-50 p-4 rounded-md text-red-600'>
          {error || 'Không tìm thấy dữ liệu'}
        </div>
      </div>
    );
  }

  // Use mapped products if available, otherwise map them on the fly
  const productItems =
    productsWithCategories.length > 0
      ? productsWithCategories
      : section.products.map((product) =>
          mapToProductItem(product, categories)
        );

  return (
    <div className='bg-gray-50 min-h-screen py-6'>
      <div className='container mx-auto px-4'>
        {/* Breadcrumb với style nổi bật hơn */}
        <nav className='mb-6'>
          <Breadcrumb
            className='p-3 bg-white shadow-sm rounded-lg border border-gray-100'
            separator={<span className='text-gray-400 mx-1'>/</span>}
            items={[
              {
                title: (
                  <Link
                    to='/'
                    className='flex items-center text-gray-600 hover:text-primary transition-colors'
                  >
                    <HomeOutlined className='mr-1' />
                    <span>Trang chủ</span>
                  </Link>
                ),
              },
              {
                title: (
                  <span className='font-medium text-primary'>
                    {section.name}
                  </span>
                ),
              },
            ]}
          />
        </nav>

        {/* Page Title */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8'>
          <div>
            <h1 className='text-2xl font-bold text-primary relative inline-block'>
              {section.name}
              <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
            </h1>
            <p className='text-gray-600 text-sm mt-1'>
              {section.products.length} sản phẩm
            </p>
          </div>
        </div>

        {/* Products Grid */}
        {section.products.length === 0 ? (
          <Empty description='Chưa có sản phẩm nào trong danh mục này' />
        ) : (
          <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3'>
            {productItems.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionPage;
